import React from 'react';
import { PlayIcon } from '@heroicons/react/24/outline';
import { PanelControls } from './PanelControls';
import { ToolNavigation } from './ToolNavigation';
import type { ViewMode, PanelSize } from './types';
import { ViewModeToggle } from './ViewModeToggle';
import { getSourceIcon } from '../../utils/sourceIcons';

import { useTranslation } from 'react-i18next';

interface ToolPanelHeaderProps {
  toolCount: number;
  viewMode: ViewMode;
  panelSize: PanelSize;
  onViewModeChange: (mode: ViewMode) => void;
  onPanelSizeChange: (size: PanelSize) => void;
  onClose: () => void;
  toolName?: string;
  toolSource?: string;
}

export const ToolPanelHeader: React.FC<ToolPanelHeaderProps> = React.memo(
  ({
    toolCount,
    viewMode,
    panelSize,
    onViewModeChange,
    onPanelSizeChange,
    onClose,
    toolName,
    toolSource,
  }: ToolPanelHeaderProps) => {
    const { t } = useTranslation('pleri/chat');
    const sourceIcon = toolSource ? getSourceIcon(toolSource) : null;

    return (
      <div className="flex h-12 items-center justify-between border-b border-gray-200/80 bg-white px-3 dark:border-gray-800 dark:bg-gray-950">
        <div className="flex items-center gap-2">
          {sourceIcon ? (
            <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-md border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-800">
              {sourceIcon}
            </div>
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-[#FF732E]">
              <PlayIcon className="h-3.5 w-3.5 text-white" />
            </div>
          )}
          <span className="max-w-[200px] truncate text-sm font-medium tracking-[-0.01em] text-gray-800 antialiased dark:text-gray-200">
            {toolName || t('toolsCount', { toolCount })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ViewModeToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
          <ToolNavigation />
          <PanelControls
            panelSize={panelSize}
            onPanelSizeChange={onPanelSizeChange}
            onClose={onClose}
          />
        </div>
      </div>
    );
  },
);

ToolPanelHeader.displayName = 'ToolPanelHeader';
